"use client"

import type { PooTransparencyEvent, PooTransparencyScalar } from '@/contracts/poo'

function timestamp(value: number): string {
  return new Date(value * 1000).toISOString().replace('T', ' ').replace('.000Z', ' UTC')
}

function shortHash(value: string): string {
  return `${value.slice(0, 12)}…${value.slice(-8)}`
}

function factLabel(value: string): string {
  if (value === 'poaEnabled') return 'Acceptance Proof enabled'
  return value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, letter => letter.toUpperCase())
}

function factValue(value: PooTransparencyScalar): string {
  if (value === null) return '—'
  if (typeof value === 'boolean') return value ? 'yes' : 'no'
  if (typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)) return shortHash(value)
  return String(value)
}

function ProtocolSub({ event }: { event: PooTransparencyEvent }) {
  return (
    <article
      data-md-contract="poo.protocol-sub"
      className="relative rounded-lg border border-[#d8d2c6] bg-white px-4 py-3 shadow-[0_1px_2px_rgba(28,40,36,0.04)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#738079]">
            Protocol sub {event.protocolSequence} · global leaf {event.logIndex + 1}
          </div>
          <h3 className="mt-1 text-sm font-semibold text-[#26332f]">{event.summary}</h3>
        </div>
        <time dateTime={new Date(event.recordedAt * 1000).toISOString()} className="text-[11px] text-[#76807c]">
          {timestamp(event.recordedAt)}
        </time>
      </div>

      <dl className="mt-3 grid gap-x-5 gap-y-2 text-xs sm:grid-cols-2">
        {Object.entries(event.payload).map(([key, value]) => (
          <div key={key} className="min-w-0">
            <dt className="text-[#7b847f]">{factLabel(key)}</dt>
            <dd className="mt-0.5 break-all font-mono text-[11px] text-[#384640]">{factValue(value)}</dd>
          </div>
        ))}
      </dl>

      <details className="mt-3 border-t border-[#ece8df] pt-2 text-xs text-[#53605b]">
        <summary className="cursor-pointer select-none font-semibold text-[#315f52]">Cryptographic log receipt</summary>
        <dl className="mt-2 grid gap-2 sm:grid-cols-2">
          <div><dt className="text-[#7b847f]">Event hash</dt><dd className="break-all font-mono text-[10px]">{event.eventHash}</dd></div>
          <div><dt className="text-[#7b847f]">Previous event</dt><dd className="break-all font-mono text-[10px]">{event.previousEventHash ?? 'first event'}</dd></div>
          <div><dt className="text-[#7b847f]">Merkle root</dt><dd className="break-all font-mono text-[10px]">{event.checkpoint.rootHash}</dd></div>
          <div><dt className="text-[#7b847f]">Previous checkpoint</dt><dd className="break-all font-mono text-[10px]">{event.checkpoint.previousRootHash}</dd></div>
          <div><dt className="text-[#7b847f]">Signed tree</dt><dd className="font-mono text-[10px]">{event.checkpoint.treeSize} leaves · {event.checkpoint.inclusionProof.length} proof nodes</dd></div>
          <div className="sm:col-span-2"><dt className="text-[#7b847f]">Checkpoint signature</dt><dd className="break-all font-mono text-[10px]">{event.checkpoint.signature}</dd></div>
        </dl>
      </details>
    </article>
  )
}

export function PooTransparencyTimeline({ events }: { events: readonly PooTransparencyEvent[] }) {
  return (
    <section data-md-contract="poo.protocol-subs" className="mt-6">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#68746f]">Append-only evidence</div>
          <h2 className="mt-1 text-lg font-semibold text-[#1d2a26]">Protocol subs</h2>
        </div>
        <p className="max-w-xl text-right text-xs leading-5 text-[#77807c]">
          Read-only events are hash-chained into the global log and bound to a signed Merkle checkpoint.
        </p>
      </div>
      {events.length ? (
        <div role="list" className="space-y-2">
          {events.map(event => <ProtocolSub key={event.eventId} event={event} />)}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-[#cfc8bb] bg-[#faf9f6] px-4 py-5 text-sm text-[#68736e]">
          No protocol events have been appended.
        </div>
      )}
    </section>
  )
}
